import { useQueries } from "@tanstack/react-query";
import type {
  ThroughputLineConfigsListResponse,
  ThroughputLineUsageFilter,
} from "@situate/contracts/runtime";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import type { AreaLocation } from "@/features/deployment-dashboard/types";
import type { ThroughputLineOption } from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";

function buildThroughputLineConfigsPath(areaId: string, usage: ThroughputLineUsageFilter): string {
  return `/v1/areas/${encodeURIComponent(areaId)}/throughput-lines?usage=${usage}`;
}

interface ThroughputLineOptionsResult {
  lines: ThroughputLineOption[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches the configured throughput lines for one or more accessible deployment areas and
 * flattens them into a single list of selectable options, each tagged with its owning area.
 * Supports the Analytics page's "All Areas" filter, where the Throughput Trend chart lets the
 * user pick any configured line across every area instead of being scoped to a single one.
 *
 * The runtime API grant is checked independently of the selected areas so an unauthorized or
 * incomplete runtime session cannot issue the request.
 */
export function useThroughputLineConfigs(
  deploymentId: string,
  areas: AreaLocation[],
  usage: ThroughputLineUsageFilter = "standard"
): ThroughputLineOptionsResult {
  const runtime = useRuntimeHttpClient();
  const hasThroughputLineConfigsGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "throughput_line_configs_list"
  );
  const client = runtime.client;
  const isEnabled =
    client !== null && runtime.deploymentId === deploymentId && hasThroughputLineConfigsGrant;

  const { lines, isLoading, error } = useQueries({
    queries: areas.map((area) => ({
      queryKey: ["throughput-line-configs", deploymentId, area.id, usage],
      queryFn: async ({ signal }) => {
        if (client === null) {
          throw new Error("Throughput line configs require a runtime client");
        }

        return client.get<ThroughputLineConfigsListResponse>(
          buildThroughputLineConfigsPath(area.id, usage),
          { signal }
        );
      },
      enabled: isEnabled,
      select: (response: ThroughputLineConfigsListResponse): ThroughputLineOption[] =>
        response.lines.map((line) => ({ ...line, areaId: area.id, areaName: area.name })),
    })),
    // `combine` lets TanStack Query memoize the combined result via structural sharing, so
    // `lines` keeps a stable reference across renders when the underlying query results
    // haven't changed. Without this, flattening the per-query results below would build a
    // brand-new array identity on every render; a consumer effect elsewhere depended on this
    // array's identity and re-ran on every render as a result, causing an infinite render loop.
    combine: (results) => ({
      lines: results.flatMap((result) => result.data ?? []),
      isLoading: results.some((result) => result.isLoading),
      error: results.find((result) => result.error != null)?.error ?? null,
    }),
  });

  return { lines, isLoading, error };
}
